import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { useDesignSchemesBucket } from '@gui/store/persistent/persistentStore';
import { Box, Checkbox, Slider, Typography } from '@mui/material';
import type { DeepPartial, OrientedVolumeDesignState } from '@visualizer/types';
import { DesignSchemeController } from '../DesignSchemeView';

export interface OrientedVolumeGuiProps {
    setId: string;
    footprintId: string;
    schemeId: string;
}

export const OrientedVolumeView: React.FC<OrientedVolumeGuiProps> = ({ setId, footprintId, schemeId }) => {
    // Zustand Variables:
    const designSchemes = useDesignSchemesBucket((s) => s);
    const scheme = designSchemes.sets?.[setId]?.footprints?.[footprintId]?.designSchemes?.[schemeId];
    const orientedVolumeState = scheme.orientedVolume;
    const defaults = designSchemes.defaults.orientedVolume;

    const active = orientedVolumeState?.active ?? defaults.active;
    const transparent = orientedVolumeState?.transparent ?? defaults.transparent;
    const concave = orientedVolumeState?.concave ?? defaults.concave;
    const concaveAlpha = orientedVolumeState?.concaveAlpha ?? defaults.concaveAlpha;
    const step = orientedVolumeState?.step ?? defaults.step;

    // Handlers:
    const updateOrientedVolume = (partial: DeepPartial<OrientedVolumeDesignState>) => {
        const controller = ControllerManager.getInstance().getDesignSchemeController();
        controller.updateDesignScheme(setId, footprintId, schemeId, {
            orientedVolume: partial,
        });
    };
    const formatOneDecimal = (value: number) => value.toFixed(1);
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <CustomTooltip
                    mode="icon"
                    side="right"
                    title={
                        <Typography
                            sx={{
                                fontSize: '10.5px',
                                lineHeight: 1.4,
                                color: 'text.secondary',
                            }}
                        >
                            Generates an oriented bounding volume subdivided into slabs, where each slab is aligned to the site's
                            predominant direction, allowing the overall massing to reflect the primary spatial orientation of the site.
                        </Typography>
                    }
                >
                    <Typography variant="subtitle1" align="center" sx={{ textAlign: 'center' }}>
                        Oriented Volume
                    </Typography>
                </CustomTooltip>
            </Box>
            <Box mt={1}>
                <DesignSchemeController schemeId={schemeId} footprintId={footprintId} setId={setId} />
            </Box>

            {active && (
                <>
                    <Box
                        mt={1}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            width: '100%',
                            minWidth: 0,
                        }}
                    >
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            Panel Width
                        </Typography>

                        <Slider
                            data-testid="ds-oriented-volume-step-slider"
                            className="panel-slider"
                            size="small"
                            value={step.value}
                            min={step.minValue}
                            max={step.maxValue}
                            step={0.1}
                            valueLabelDisplay="on"
                            valueLabelFormat={formatOneDecimal}
                            onChange={(_, value) => {
                                const stepValue = Array.isArray(value) ? value[0] : value;
                                updateOrientedVolume({
                                    step: { ...step, value: stepValue },
                                });
                            }}
                            sx={{ flex: '1 1 0', minWidth: 0 }}
                        />
                        <Typography variant="body2" whiteSpace="nowrap" sx={{ ml: 0.5, textAlign: 'right', flex: '0 0 auto' }}>
                            [m]
                        </Typography>
                    </Box>

                    <Box mt={1} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Checkbox
                            data-testid="ds-oriented-volume-transparent-checkbox"
                            size="small"
                            checked={transparent}
                            onChange={(e) => updateOrientedVolume({ transparent: e.target.checked })}
                            sx={{ padding: 0, '& .MuiSvgIcon-root': { fontSize: '14px' } }}
                        />
                        <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Transparent</Typography>
                        <Checkbox
                            data-testid="ds-oriented-volume-concave-checkbox"
                            size="small"
                            checked={concave}
                            onChange={(e) => updateOrientedVolume({ concave: e.target.checked })}
                            sx={{ padding: 0, '& .MuiSvgIcon-root': { fontSize: '14px' } }}
                        />
                        <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Concave</Typography>
                    </Box>

                    {concave && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                                minWidth: 0,
                                mt: 1,
                            }}
                        >
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                Concaveness
                            </Typography>

                            <Slider
                                data-testid="ds-oriented-volume-concaveness-slider"
                                className="panel-slider"
                                size="small"
                                value={concaveAlpha.value}
                                min={concaveAlpha.minValue}
                                max={concaveAlpha.maxValue}
                                step={0.1}
                                valueLabelDisplay="on"
                                valueLabelFormat={formatOneDecimal}
                                onChange={(_, value) => {
                                    const nextValue = Array.isArray(value) ? value[0] : value;
                                    updateOrientedVolume({
                                        concaveAlpha: { ...concaveAlpha, value: nextValue },
                                    });
                                }}
                                sx={{ flex: '1 1 0', minWidth: 0 }}
                            />
                            <Typography variant="body2" whiteSpace="nowrap" sx={{ ml: 0.5, textAlign: 'right', flex: '0 0 auto' }}>
                                [a]
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};
